import { QuestionnaireAnswer } from '@capturi/api-conversations'
import request, { ResponseError } from '@capturi/request'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

type QuestionAnswer =
  | { type: 'freeText'; answer: string }
  | { type: 'yesNo'; answer: 'Yes' | 'No' }
  | { type: 'range'; answer: number }
  | { type: 'notRelevant' }

const trimAnswer = (answer: QuestionAnswer): QuestionAnswer =>
  answer.type === 'freeText'
    ? { ...answer, answer: answer.answer.trim() }
    : answer

const trimAnswers = (answers: Record<string, QuestionAnswer>) =>
  Object.fromEntries(
    Object.entries(answers).map(([k, a]) => [k, trimAnswer(a)]),
  )

export type QuestionnaireAnswersInput = Record<string, QuestionAnswer>

// Helper to update questionnaire answers cache
const useQuestionnaireAnswersCache = (conversationUid: string) => {
  const queryClient = useQueryClient()
  const queryKey = [
    'questionnaires',
    'answers',
    'conversation',
    conversationUid,
  ]

  return {
    addAnswer: (answer: QuestionnaireAnswer) => {
      queryClient.setQueryData<QuestionnaireAnswer[]>(queryKey, (oldData) => {
        if (!oldData) return [answer]
        return [...oldData, answer]
      })
    },
    updateAnswer: (updatedAnswer: QuestionnaireAnswer) => {
      queryClient.setQueryData<QuestionnaireAnswer[]>(queryKey, (oldData) => {
        if (!oldData) return [updatedAnswer]
        return oldData.map((answer) =>
          answer.uid === updatedAnswer.uid ? updatedAnswer : answer,
        )
      })
    },
    removeAnswer: (answerUid: string) => {
      queryClient.setQueryData<QuestionnaireAnswer[]>(queryKey, (oldData) => {
        if (!oldData) return []
        return oldData.filter((answer) => answer.uid !== answerUid)
      })
    },
  }
}

// Fetch questionnaire answers for a conversation
export const useConversationAnswers = (conversationUid?: string) => {
  return useQuery<QuestionnaireAnswer[]>({
    queryKey: ['questionnaires', 'answers', 'conversation', conversationUid],
    queryFn: async () => {
      if (!conversationUid) return []

      const response = await request.get<{
        answers: QuestionnaireAnswer[]
      }>(`questionnaires/answer/conversation/${conversationUid}`, {
        query: { 'api-version': '3.3' },
      })

      return response.answers || []
    },
    enabled: !!conversationUid,
  })
}

export const useCreateAnswer = ({
  conversationUid,
  userUid,
}: {
  conversationUid: string
  userUid: string
}) => {
  const cache = useQuestionnaireAnswersCache(conversationUid)

  return useMutation<
    QuestionnaireAnswer,
    ResponseError,
    { questionnaireUid: string; answers: QuestionnaireAnswersInput }
  >({
    mutationFn: async ({ questionnaireUid, answers }) => {
      const processedAnswers = trimAnswers(answers)

      const response = await request.post<QuestionnaireAnswer>(
        'questionnaires/answer',
        {
          query: { 'api-version': '3.3' },
          json: {
            answers: processedAnswers,
            questionnaireUid,
            conversationUid,
            userUid,
          },
        },
      )

      return response
    },
    onSuccess: (createdAnswer) => {
      cache.addAnswer(createdAnswer)
    },
  })
}

// Update existing answer
export const useUpdateAnswer = ({
  conversationUid,
}: {
  conversationUid: string
}) => {
  const cache = useQuestionnaireAnswersCache(conversationUid)

  return useMutation<
    QuestionnaireAnswer,
    ResponseError,
    {
      answerUid: string
      updates: QuestionnaireAnswersInput
    }
  >({
    mutationFn: async ({ answerUid, updates }) => {
      const processedUpdates = trimAnswers(updates)

      const response = await request.patch<QuestionnaireAnswer>(
        `questionnaires/answer/${answerUid}`,
        {
          query: { 'api-version': '3.3' },
          json: { answers: processedUpdates },
        },
      )

      return response
    },
    onSuccess: (updatedAnswer) => {
      cache.updateAnswer(updatedAnswer)
    },
  })
}

// Delete answer
export const useDeleteAnswer = ({
  conversationUid,
}: {
  conversationUid: string
}) => {
  const cache = useQuestionnaireAnswersCache(conversationUid)

  return useMutation<void, ResponseError, { answerUid: string }>({
    mutationFn: async ({ answerUid }) => {
      await request.delete(`questionnaires/answer/${answerUid}`, {
        query: { 'api-version': '3.3' },
      })
    },
    onSuccess: (_, { answerUid }) => {
      cache.removeAnswer(answerUid)
    },
  })
}
